import { Injectable } from "@angular/core";
import { DeferredPromise } from "@cf-shared/shared-util";
import { LoadingHostComponent } from "components/loading-host/loading-host";
import { TranslatedString } from "models/translated-string";
import { WakeLockService } from "services/wake-lock.service";

export interface LoadingPopoverOptions {
  content?: TranslatedString;
  // hold a screen wake-lock for the lifetime of this loader: acquire on
  // present(), release on dismiss(). default false. opt-in (per plan
  // option (b)) so very-short loaders don't grab the lock unnecessarily.
  // each opted-in loader uses its own tag, so overlapping loaders coexist
  // correctly via WakeLockService's refcount.
  keepScreenAwake?: boolean;
}

export class Loading {
  loadingController: LoadingController;
  opts: LoadingPopoverOptions;
  // tag handed to WakeLockService; set iff opts.keepScreenAwake is true.
  // present() populates it, dismiss() consumes-and-clears so repeated
  // dismiss calls don't double-release. The controller's in-process
  // counter gives a collision-free per-loader identity for the lifetime
  // of the controller (no platform dependency on crypto.randomUUID,
  // which requires Safari 15.4+ / Chrome 92+).
  private wakeLockTag: string | undefined;

  constructor(loadingController: LoadingController, opts: LoadingPopoverOptions) {
    this.loadingController = loadingController;
    this.opts = opts;
  }

  // push this loader onto the controller's stack and route it through the
  // loading host. if opted into keepScreenAwake, mint a per-loader tag and
  // acquire the wake-lock (release is paired in dismiss()).
  async present() {
    this.loadingController.loadings.push(this);
    if (this.opts?.keepScreenAwake && !this.wakeLockTag) {
      this.wakeLockTag = `loader:${++this.loadingController.nextWakeLockSeq}`;
      void this.loadingController.wakeLock.acquireAsync(this.wakeLockTag);
    }
    return this.loadingController.loadingHost.setLoading(this);
  }

  // remove this loader from the stack and release its wake-lock tag (if any),
  // then update the host to show the next-most-recent loader or clear it.
  // safe to call twice — indexOf-guarded splice + tag consume-and-clear.
  async dismiss() {
    // guard against indexOf -1 (double-dismiss or never-presented): splice(-1, 1)
    // would silently remove the last stack entry — a totally different loader.
    const idx = this.loadingController.loadings.indexOf(this);
    if (idx !== -1) {
      this.loadingController.loadings.splice(idx, 1);
    }
    if (this.wakeLockTag) {
      const tag = this.wakeLockTag;
      this.wakeLockTag = undefined;
      void this.loadingController.wakeLock.releaseAsync(tag);
    }
    if (this.loadingController.loadings.length === 0) {
      this.loadingController.isLoadingActive?.resolve();
      this.loadingController.isLoadingActive = undefined;
      return this.loadingController.loadingHost.setLoading(undefined);
    } else {
      return this.loadingController.loadingHost.setLoading(this.loadingController.loadings[this.loadingController.loadings.length - 1]);
    }
  }

  setContent(content: TranslatedString) {
    this.opts.content = content;
  }
}

@Injectable()
export class LoadingController {
  loadingHost: LoadingHostComponent = undefined;

  loadings: Loading[] = [];

  // monotonic counter that mints unique `loader:<n>` wake-lock tags for
  // loaders opted into keepScreenAwake. In-process counter avoids the
  // platform dependency that `crypto.randomUUID()` would carry (Safari
  // 15.4+, Chrome 92+).
  nextWakeLockSeq = 0;

  isLoadingActive: DeferredPromise<void>;

  constructor(readonly wakeLock: WakeLockService) {

  }

  isLoadingPromise() {
    if (this.loadings.length === 0) {
      return Promise.resolve();
    }
    if (!this.isLoadingActive) {
      this.isLoadingActive = DeferredPromise.defer<void>();
    }
    return this.isLoadingActive;
  }

  public registerHost(host: LoadingHostComponent) {
    this.loadingHost = host;
  }

  create(opts?: LoadingPopoverOptions) {
    return new Loading(this, opts);
  }

}
